// RtfNoteFloat.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactQuill from 'react-quill';
//import 'react-quill/dist/quill.snow.css';
import "./RtfNoteFloat.css";
import { useTheme } from '../contexts/ThemeContext';
import { Bold, Italic, Code, List, Image, Type, Palette } from 'lucide-react';
// Add this import near the other imports
import { useEditor } from '../contexts/EditorContext';
import FloatingFormatBar from '../FloatingFormatBar';
import { debounce } from 'lodash';

const Font = ReactQuill.Quill.import('formats/font');
Font.whitelist = ['monospace', 'serif', 'sans-serif', 'fira-sans', 'playfair', 'roboto', 'oswald', 'inconsolata'];
ReactQuill.Quill.register(Font, true);

const Size = ReactQuill.Quill.import('formats/size');
Size.whitelist = ['small', 'normal', 'large', 'huge'];
ReactQuill.Quill.register(Size, true);

const Color = ReactQuill.Quill.import('formats/color');
ReactQuill.Quill.register(Color, true);

//console.log('ReactQuill Version:', ReactQuill.version);
//console.log('Quill Core Version:', ReactQuill.Quill.version);

const RtfNoteFloat = ({ 
  initialValue = '', 
  onChange, 
  noteId,
  placeholder = "Write your content...", 
  categories, 
  selectedCategoryId, 
  onCategoryChange 
}) => {
  const [editorKey, setEditorKey] = useState(0);
  const [value, setValue] = useState('');
  const [editorInstance, setEditorInstance] = useState(null);
  const quillRef = useRef(null);
  const editorId = useRef(`editor-${Math.random().toString(36).substr(2, 9)}`).current;
  const theme = useTheme();
  const previousValueRef = useRef('');
  const { setEditor } = useEditor();

  // Instead of a single flag, track code sections
  const hasCodeSectionsRef = useRef(false);
  const codeSectionsRef = useRef([]);

  const debouncedSave = useCallback(
    debounce((content) => {
      if (onChange && noteId) {
        onChange(content);
      }
    }, 2000),
    [onChange, noteId]
  );

  useEffect(() => {
    if (!quillRef.current) return;
  
    const editor = quillRef.current.getEditor();
    const currentContents = editor.root.innerHTML;
  
    // Prevent unnecessary updates if content is the same
    if (currentContents === initialValue) {
      return;
    }
  
    // Preserve cursor position before updating
    const range = editor.getSelection();
  
    // Parse the new note content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = initialValue;
  
    // Find all code blocks
    const codeBlocks = tempDiv.querySelectorAll('pre.ql-syntax');
    const codeSections = Array.from(codeBlocks).map(block => ({
      content: block.outerHTML,
      start: tempDiv.innerHTML.indexOf(block.outerHTML),
      end: tempDiv.innerHTML.indexOf(block.outerHTML) + block.outerHTML.length,
    }));
  
    // Store code sections reference
    codeSectionsRef.current = codeSections;
    hasCodeSectionsRef.current = codeSections.length > 0;
  
    // Fix: Ensure Quill Resets Correctly
    if (hasCodeSectionsRef.current) {
      // If switching to a code note, clear existing formatting
      editor.removeFormat(0, editor.getLength());
    } else {
      // Force Quill to reset styles when switching FROM a code note
      editor.format('code-block', false);
    }
  
    // Safely paste new content
    editor.clipboard.dangerouslyPasteHTML(initialValue);
  
    // Restore cursor position
    if (range) {
      editor.setSelection(range.index, range.length);
    }
  
    // Fix: Ensure code formatting is only applied if necessary
    if (hasCodeSectionsRef.current) {
      setTimeout(() => {
        const editorContent = editor.root.innerHTML;
        codeSections.forEach(section => {
          const start = editorContent.indexOf(section.content);
          if (start !== -1) {
            editor.formatText(start, section.content.length, 'code-block', true);
          }
        });
      }, 0);
    }
  }, [initialValue]);

  useEffect(() => {
    if (!quillRef.current) return;
    
    const editor = quillRef.current.getEditor();
    if (editor) {
      setEditor(editor);
      setEditorInstance(editor);
      //console.log('Editor instance set:', editor);
      
      // Add drop handlers to the editor's DOM element
      const editorElement = editor.root;
      
      const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
      };

      const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const droppedText = e.dataTransfer.getData('text/plain');
        if (!droppedText.trim()) return;

        const selection = editor.getSelection();
        const insertIndex = selection ? selection.index : editor.getLength();

        editor.insertText(insertIndex, droppedText);
        editor.setSelection(insertIndex + droppedText.length);
      };
      
      editorElement.addEventListener('dragover', handleDragOver);
      editorElement.addEventListener('drop', handleDrop);
      
      return () => {
        editorElement.removeEventListener('dragover', handleDragOver);
        editorElement.removeEventListener('drop', handleDrop);
        setEditor(null);
        setEditorInstance(null);
      };
    }
    
    return () => {
      setEditor(null);
      setEditorInstance(null);
    };
  }, [setEditor]);

  const handleChange = (content, delta, source, editor) => {
    if (content === previousValueRef.current) {
      return;
    }

    // Preserve code sections if they exist
    if (hasCodeSectionsRef.current) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      
      // Ensure code blocks have proper classes
      const codeBlocks = tempDiv.querySelectorAll('pre');
      codeBlocks.forEach(block => {
        if (!block.classList.contains('ql-syntax')) {
          block.classList.add('ql-syntax');
          block.setAttribute('spellcheck', 'false');
        }
      });
      
      content = tempDiv.innerHTML;
    }

    setValue(content);
    previousValueRef.current = content;

   
      debouncedSave(content);
    
  };

  const modules = {
    toolbar: false,
    clipboard: {
      matchVisual: false
    },
    history: {
      delay: 2000,
      maxStack: 500,
      userOnly: true
    }
  };

  const handleFormatBarSave = useCallback((content) => {
    if (onChange && noteId) {
      onChange(content);
    }
  }, [onChange, noteId]);

  useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  return (
    <div className={`notion-style-editor ${editorId}`} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <FloatingFormatBar 
        editor={editorInstance} 
        onSave={handleFormatBarSave}
        noteId={noteId} 
      />
      <ReactQuill
        ref={quillRef}
        theme="snow"
        defaultValue={value}
        onChange={handleChange}
        modules={modules}
        placeholder={placeholder}
        className="notion-editor"
        style={{ flex: 1 }}
      />
    </div>
  );
};

export default React.memo(RtfNoteFloat);